/**
 * EmployeeMobileDashboard — always-open mobile-first attendance hub.
 * Employees check in/out here with GPS. Works on phone and desktop.
 * Auto-refreshes every 60s to stay current.
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../../services/api";

// ── Types ──────────────────────────────────────────────────────────────────────

interface ShiftLog {
  shift_type: string;
  actual_time: string;
  minutes_offset: number;
  tactical_delay: boolean;
  device_type: string | null;
  has_location: boolean;
}

interface NextShift {
  shift_type: string;
  label: string;
  scheduled: string;
  minutes_until: number;
  overdue: boolean;
}

interface KPI {
  attendance_rate: number;
  on_time_count: number;
  late_count: number;
  total_check_ins: number;
  scheduled_hours: number;
  actual_hours_present: number;
}

interface DashboardData {
  employee_name: string;
  date: string;
  is_friday: boolean;
  server_time: string;
  today_shifts: ShiftLog[];
  next_shift: NextShift | null;
  kpi_30d: KPI;
}

interface LocationState {
  lat: number | null;
  lon: number | null;
  error: string | null;
  loading: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const SHIFT_LABELS: Record<string, string> = {
  morning_in:  "Morning In",
  morning_out: "Morning Out",
  evening_in:  "Evening In",
  evening_out: "Evening Out",
};

const SHIFT_ICONS: Record<string, string> = {
  morning_in:  "🌅",
  morning_out: "☀️",
  evening_in:  "🌆",
  evening_out: "🌙",
};

const ALL_SHIFTS = ["morning_in", "morning_out", "evening_in", "evening_out"];

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-US", {
    timeZone: "Asia/Dubai",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function offsetLabel(offset: number, tactical: boolean) {
  if (tactical) return `⚠️ ${Math.abs(offset).toFixed(0)}m late`;
  if (offset < -15) return `⚡ ${Math.abs(offset).toFixed(0)}m early`;
  return "✅ On time";
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  employeeName: string;
}

const EmployeeMobileDashboard: React.FC<Props> = ({ employeeName }) => {
  const [data, setData]               = useState<DashboardData | null>(null);
  const [loading, setLoading]         = useState(true);
  const [checkingIn, setCheckingIn]   = useState(false);
  const [lastAction, setLastAction]   = useState<string | null>(null);
  const [loc, setLoc]                 = useState<LocationState>({ lat: null, lon: null, error: null, loading: false });
  const [now, setNow]                 = useState(new Date());
  const intervalRef                   = useRef<number | null>(null);
  const pingIntervalRef               = useRef<number | null>(null);

  // ── Live clock ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // ── Auto-ping every 10 minutes to signal online presence ───────────────────
  useEffect(() => {
    const doPing = () => {
      fetch("/api/employee/ping", { credentials: "include" }).catch(() => {});
    };
    doPing(); // ping immediately on mount
    pingIntervalRef.current = window.setInterval(doPing, 10 * 60 * 1000);
    return () => { if (pingIntervalRef.current) clearInterval(pingIntervalRef.current); };
  }, []);

  // ── Fetch dashboard data ────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    try {
      const d = await api.employee.myDashboard(employeeName);
      setData(d);
    } catch {
      // keep stale data on error
    } finally {
      setLoading(false);
    }
  }, [employeeName]);

  useEffect(() => {
    refresh();
    intervalRef.current = window.setInterval(refresh, 60_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [refresh]);

  // ── GPS request ─────────────────────────────────────────────────────────────
  const requestLocation = useCallback((): Promise<{ lat: number; lon: number } | null> => {
    return new Promise(resolve => {
      if (!navigator.geolocation) {
        setLoc(l => ({ ...l, error: "GPS not available on this device." }));
        resolve(null);
        return;
      }
      setLoc(l => ({ ...l, loading: true, error: null }));
      navigator.geolocation.getCurrentPosition(
        pos => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          setLoc({ lat, lon, error: null, loading: false });
          resolve({ lat, lon });
        },
        err => {
          setLoc(l => ({ ...l, loading: false, error: "Location denied — checking in without GPS." }));
          resolve(null);
        },
        { timeout: 8000, maximumAge: 30_000 }
      );
    });
  }, []);

  // ── Check-in ────────────────────────────────────────────────────────────────
  const handleCheckIn = async (shiftType: string) => {
    setCheckingIn(true);
    setLastAction(null);

    const position = await requestLocation();

    // Detect device type client-side
    const ua = navigator.userAgent.toLowerCase();
    let deviceType = "desktop";
    if (/iphone|android|mobile|blackberry|windows phone/.test(ua)) deviceType = "mobile";
    else if (/ipad|tablet/.test(ua)) deviceType = "tablet";

    try {
      const res = await api.employee.checkIn({
        employee_name: employeeName,
        shift_type:    shiftType,
        latitude:      position?.lat ?? null,
        longitude:     position?.lon ?? null,
        device_type:   deviceType,
      });
      setLastAction(`${SHIFT_LABELS[shiftType]} recorded — ${res.message}`);
      await refresh();
    } catch (e: any) {
      setLastAction(`Error: ${e.message}`);
    } finally {
      setCheckingIn(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const dubaiTime = now.toLocaleTimeString("en-US", {
    timeZone: "Asia/Dubai",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const dubaiDate = now.toLocaleDateString("en-US", {
    timeZone: "Asia/Dubai",
    weekday: "long",
    month:   "long",
    day:     "numeric",
  });

  const doneSifts = new Set(data?.today_shifts.map(s => s.shift_type) ?? []);
  const kpi       = data?.kpi_30d;

  if (loading) {
    return (
      <div style={styles.fullScreen}>
        <div style={styles.spinner}>⏳</div>
        <p style={{ color: "#ffd6a5", marginTop: 16, fontFamily: "Montserrat, sans-serif" }}>Loading your dashboard…</p>
      </div>
    );
  }

  return (
    <div style={styles.fullScreen}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerName}>{employeeName.split(" ")[0]}</div>
        <div style={styles.clock}>{dubaiTime}</div>
        <div style={styles.dateLabel}>{dubaiDate} · Dubai</div>
      </div>

      {/* KPI Strip */}
      {kpi && (
        <div style={styles.kpiStrip}>
          <div style={styles.kpiItem}>
            <span style={styles.kpiValue}>{kpi.attendance_rate.toFixed(0)}%</span>
            <span style={styles.kpiLabel}>Attendance</span>
          </div>
          <div style={styles.kpiDivider} />
          <div style={styles.kpiItem}>
            <span style={styles.kpiValue}>{kpi.on_time_count}</span>
            <span style={styles.kpiLabel}>On Time</span>
          </div>
          <div style={styles.kpiDivider} />
          <div style={styles.kpiItem}>
            <span style={styles.kpiValue}>{kpi.late_count}</span>
            <span style={styles.kpiLabel}>Late</span>
          </div>
          <div style={styles.kpiDivider} />
          <div style={styles.kpiItem}>
            <span style={styles.kpiValue}>{kpi.actual_hours_present.toFixed(1)}h</span>
            <span style={styles.kpiLabel}>30d Hrs</span>
          </div>
        </div>
      )}

      {/* Next shift banner */}
      {data?.next_shift && (
        <div style={{
          ...styles.nextShiftBanner,
          background: data.next_shift.overdue
            ? "linear-gradient(135deg, rgba(239,68,68,0.15), rgba(220,38,38,0.1))"
            : "linear-gradient(135deg, rgba(184,213,141,0.15), rgba(189,178,255,0.1))",
          borderColor: data.next_shift.overdue ? "rgba(239,68,68,0.4)" : "rgba(184,213,141,0.3)",
        }}>
          <span style={{ fontSize: 22 }}>{SHIFT_ICONS[data.next_shift.shift_type]}</span>
          <div>
            <div style={{ color: "#f8fafc", fontWeight: 700, fontSize: 15 }}>
              {data.next_shift.label}
            </div>
            <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>
              {data.next_shift.overdue
                ? `⚠️ Overdue by ${Math.abs(data.next_shift.minutes_until).toFixed(0)} min`
                : data.next_shift.minutes_until <= 0
                  ? "Now"
                  : `In ${data.next_shift.minutes_until.toFixed(0)} min · ${fmtTime(data.next_shift.scheduled)}`}
            </div>
          </div>
        </div>
      )}
      {!data?.next_shift && (
        <div style={{ ...styles.nextShiftBanner, borderColor: "rgba(184,213,141,0.3)" }}>
          <span style={{ fontSize: 22 }}>✅</span>
          <div style={{ color: "#b8d58d", fontWeight: 700, fontSize: 15 }}>All shifts done for today!</div>
        </div>
      )}

      {/* Shift buttons */}
      <div style={styles.shiftsGrid}>
        {ALL_SHIFTS.map(shift => {
          const done = doneSifts.has(shift);
          const detail = data?.today_shifts.find(s => s.shift_type === shift);
          return (
            <button
              key={shift}
              style={{
                ...styles.shiftBtn,
                ...(done ? styles.shiftBtnDone : styles.shiftBtnPending),
                cursor: done || checkingIn ? "default" : "pointer",
                opacity: checkingIn && !done ? 0.7 : 1,
              }}
              disabled={done || checkingIn}
              onClick={() => !done && handleCheckIn(shift)}
            >
              <span style={{ fontSize: 24, marginBottom: 6 }}>{SHIFT_ICONS[shift]}</span>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{SHIFT_LABELS[shift]}</span>
              {done && detail && (
                <>
                  <span style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{fmtTime(detail.actual_time)}</span>
                  <span style={{ fontSize: 11, marginTop: 2 }}>{offsetLabel(detail.minutes_offset, detail.tactical_delay)}</span>
                  {detail.has_location && <span style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>📍 Location saved</span>}
                  {detail.device_type && (
                    <span style={{ fontSize: 10, color: "#64748b" }}>
                      {detail.device_type === "mobile" ? "📱" : detail.device_type === "tablet" ? "📟" : "💻"} {detail.device_type}
                    </span>
                  )}
                </>
              )}
              {!done && (
                <span style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                  {checkingIn ? "Checking in…" : "Tap to check in"}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Location status */}
      {loc.error && (
        <div style={styles.locAlert}>{loc.error}</div>
      )}
      {loc.lat && loc.lon && (
        <div style={styles.locSuccess}>
          📍 {loc.lat.toFixed(5)}, {loc.lon.toFixed(5)}
        </div>
      )}

      {/* Last action */}
      {lastAction && (
        <div style={{
          ...styles.actionFeedback,
          background: lastAction.startsWith("Error")
            ? "rgba(239,68,68,0.1)"
            : "rgba(184,213,141,0.1)",
          borderColor: lastAction.startsWith("Error")
            ? "rgba(239,68,68,0.3)"
            : "rgba(184,213,141,0.3)",
        }}>
          {lastAction}
        </div>
      )}

      {/* Refresh button */}
      <button style={styles.refreshBtn} onClick={refresh}>
        ↻ Refresh
      </button>

      {/* Footer hint */}
      <div style={styles.footer}>
        Add this page to your home screen for instant access
      </div>
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  fullScreen: {
    minHeight: "100vh",
    background: "#0f172a",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "24px 16px 40px",
    fontFamily: "Montserrat, sans-serif",
    boxSizing: "border-box",
    overflowX: "hidden",
  },
  spinner: {
    fontSize: 40,
    animation: "spin 1s linear infinite",
  },
  header: {
    width: "100%",
    maxWidth: 480,
    textAlign: "center",
    marginBottom: 20,
  },
  headerName: {
    fontSize: 28,
    fontWeight: 800,
    color: "#ffd6a5",
    letterSpacing: "0.02em",
  },
  clock: {
    fontSize: 42,
    fontWeight: 300,
    color: "#f8fafc",
    letterSpacing: "0.05em",
    marginTop: 4,
    fontVariantNumeric: "tabular-nums",
  },
  dateLabel: {
    fontSize: 13,
    color: "#64748b",
    marginTop: 4,
  },
  kpiStrip: {
    width: "100%",
    maxWidth: 480,
    display: "flex",
    justifyContent: "space-around",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(189,178,255,0.15)",
    borderRadius: 16,
    padding: "14px 8px",
    marginBottom: 16,
  },
  kpiItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 800,
    color: "#bdb2ff",
  },
  kpiLabel: {
    fontSize: 10,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  kpiDivider: {
    width: 1,
    background: "rgba(255,255,255,0.08)",
    alignSelf: "stretch",
  },
  nextShiftBanner: {
    width: "100%",
    maxWidth: 480,
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "14px 18px",
    borderRadius: 14,
    border: "1px solid",
    marginBottom: 20,
  },
  shiftsGrid: {
    width: "100%",
    maxWidth: 480,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 16,
  },
  shiftBtn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "18px 12px",
    borderRadius: 16,
    border: "1px solid",
    transition: "all 0.2s",
    fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
    background: "transparent",
    textAlign: "center",
  },
  shiftBtnDone: {
    borderColor: "rgba(184,213,141,0.4)",
    background: "rgba(184,213,141,0.08)",
    color: "#b8d58d",
  },
  shiftBtnPending: {
    borderColor: "rgba(255,214,165,0.3)",
    background: "rgba(255,214,165,0.06)",
    color: "#ffd6a5",
  },
  locAlert: {
    width: "100%",
    maxWidth: 480,
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.25)",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 12,
    color: "#fca5a5",
    marginBottom: 12,
    textAlign: "center",
  },
  locSuccess: {
    width: "100%",
    maxWidth: 480,
    background: "rgba(34,197,94,0.07)",
    border: "1px solid rgba(34,197,94,0.2)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 11,
    color: "#86efac",
    marginBottom: 12,
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
  },
  actionFeedback: {
    width: "100%",
    maxWidth: 480,
    padding: "12px 16px",
    borderRadius: 12,
    border: "1px solid",
    fontSize: 13,
    color: "#f8fafc",
    marginBottom: 16,
    textAlign: "center",
  },
  refreshBtn: {
    background: "rgba(189,178,255,0.1)",
    border: "1px solid rgba(189,178,255,0.25)",
    borderRadius: 10,
    color: "#bdb2ff",
    fontFamily: "Montserrat, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    padding: "10px 28px",
    cursor: "pointer",
    marginBottom: 24,
  },
  footer: {
    fontSize: 11,
    color: "#334155",
    textAlign: "center",
  },
};

export default EmployeeMobileDashboard;
